import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import {
  type MultiMessageHandling,
  MultiMessageHandlingSchema
} from "./MultiMessageHandling.schema";

export type { MultiMessageHandling };

/**
 * Base settings shared by all deployment types.
 * These fields appear at the top of every deployment form.
 *
 * NOTE: agentId was removed from deployment settings JSON.
 * Agent selection now uses Deployment.agentId column directly (FK to Agent).
 * This field was migrated from the JSON settings blob to a dedicated column.
 */
export const BaseSettingsSchema = z.object({
  /** Optional custom name for the deployment */
  name: z.string().max(100).optional(),

  /** Optional description for internal reference */
  description: z.string().max(500).optional(),

  /**
   * Multi-message handling strategy — shared across all conversational deployments.
   * Configures debounce window and concurrency behaviour when the agent is busy.
   * See MultiMessageHandling.schema.ts for full documentation.
   *
   * Optional on the WRITE shape — a caller creating a deployment is not required
   * to choose a concurrency strategy. Reads go through `BaseSettingsReadSchema`
   * below, which supplies one.
   */
  multiMessageHandling: MultiMessageHandlingSchema.optional(),

  /**
   * Controls how the agent responds to incoming messages for new chats.
   * Mirrors the ChatResponseHandling Prisma enum and is copied into chat.responseHandling
   * when a new chat is created for this deployment.
   *
   * AUTO        — agent runs and replies immediately (default)
   * ON_APPROVAL — agent runs but the reply is held for human approval before sending
   * MANUAL      — agent does not run; messages are stored for human handling only
   *
   * Omitted (undefined) is treated as AUTO so existing deployments are unaffected.
   */
  defaultResponseHandling: z.enum(DbEnum.CHAT_RESPONSE_HANDLING_VALUES).optional()
});

export type BaseSettings = z.infer<typeof BaseSettingsSchema>;

/**
 * Extended base settings that include initial message.
 * Used by Email, Telegram, and other conversational deployments.
 */
export const BaseSettingsWithMessageSchema = BaseSettingsSchema.extend({
  /** First message sent to users when they start a conversation */
  initialMessage: z.string().max(2000).optional()
});

export type BaseSettingsWithMessage = z.infer<typeof BaseSettingsWithMessageSchema>;

/**
 * The READ shape of the shared base settings — parse stored `deploymentSettings`
 * with this, never with `BaseSettingsSchema`.
 *
 * A stored blob carrying no `multiMessageHandling` key resolves here to
 * `{ concurrencyStrategy: SEQUENTIAL }` with **no** `debounceMs`, so an inbound
 * message is ordered against a turn already in flight instead of racing it.
 * Absent the default, `ReceiveMessageUseCase` read `null` and took the immediate
 * path with no concurrency control at all — two messages, two concurrent
 * `assistant.invoke` calls on one chat, each on its own history snapshot.
 *
 * **No `debounceMs`, deliberately.** `InterruptionStrategyRegistry.resolve`
 * branches on `debounceMs !== undefined` FIRST and unconditionally, and the
 * debounce adapter's only early exits are `PARALLEL` and a Redis-memory breaker —
 * neither asks whether an agent is running. Defaulting the full
 * `DEFAULT_MULTI_MESSAGE_HANDLING` would therefore delay a LONE message on an idle
 * chat by the whole 3s window. Sequential-with-no-debounce routes instead to the
 * sequential adapter's atomic Lua `EXISTS running` buffer: ordering guaranteed,
 * zero added latency.
 *
 * **`.prefault({})` and NOT `.default({})`.** On zod 4 `.default()` takes the
 * OUTPUT type and is returned WITHOUT being parsed, so `.default({})` does not
 * compile (TS2769 — `concurrencyStrategy` is required) and, forced through, would
 * yield a bare `{}`; `resolve`'s `switch` has no default arm, so `{}` returns
 * `undefined` and the caller throws on `port.handle()`. `.prefault({})` feeds `{}`
 * THROUGH the schema, so the value derives from `concurrencyStrategy`'s own
 * `.default(SEQUENTIAL)` and cannot drift from it.
 *
 * Kept separate from `BaseSettingsSchema` because that one is also the WRITE
 * shape: making the field required there forces every caller creating a
 * deployment — the frontend create views included — to send a concurrency
 * strategy it has no opinion about.
 */
export const BaseSettingsReadSchema = BaseSettingsSchema.extend({
  multiMessageHandling: MultiMessageHandlingSchema.prefault({})
});

export type BaseSettingsRead = z.infer<typeof BaseSettingsReadSchema>;
